(function () {
  // HDN1794-1431: 客注 cart attribute keys（c_main-cart.js OWNED_ATTR_KEYS と同じ集合）。
  // logout / session-expiry / clear-member で '' クリアする対象。両リストは import せず意図的に
  // 重複保持（片方を変えたらもう片方も同期すること = sync-comment）。test (window.CStaffModeHeaderBar)
  // で所有契約を guard するため、下の early-return guard の前に export しておく。
  const STAFF_MODE_ATTR_KEYS = [
    '_order_type',
    'selected_local_pickup_store',
    '_pickup_store_name',
    '_pickup_store_address',
    '_staff_mode',
    'phil_hide_point_form',
    'selected_order_retail_location',
  ];
  window.CStaffModeHeaderBar = { STAFF_MODE_ATTR_KEYS, cartUpdateUrl };

  const root = document.querySelector('[data-staff-mode-header-bar]');
  if (!root) return;

  const apiBase = root.dataset.apiBase || '/apps/store';
  const loginUrl = root.dataset.loginUrl;
  // Redirect-loop guard (F3): survives the login<->EC round trip within the same tab so a
  // /staff-mode/store 401 caused by something other than real token expiry (endpoint bug, proxy
  // misconfig...) doesn't bounce the staff between EC and the login page forever. Cleared once a
  // validate call succeeds again (see validateStaffSession).
  const SESSION_EXPIRED_GUARD_KEY = 'c_staff_mode_expired_redirect';
  // Staff-mode auth lives in a session cookie (cross-tab, auto-cleared on browser close; spec §2.2.3/§2.1.5).
  // App Proxy strips Set-Cookie so an HttpOnly cookie is impossible; the token must be
  // JS-readable anyway to send the X-Staff-Mode-Token header. "In staff mode" = token present.
  // All cookie I/O goes through window.CStaffSession (c_staff-session.js, loaded first via head defer).
  let accessToken = (window.CStaffSession && CStaffSession.getToken()) || '';

  const storeName = root.querySelector('[data-staff-mode-store-name]');
  const menuButton = root.querySelector('[data-staff-mode-menu-button]');
  const menuPanel = root.querySelector('[data-staff-mode-menu-panel]');
  const clearButton = root.querySelector('[data-staff-mode-clear-customer]');
  const logoutButton = root.querySelector('[data-staff-mode-logout]');
  const logoutLabel = root.querySelector('[data-staff-mode-logout-label]');
  const logoutSpinner = root.querySelector('.c_staff-mode-header-bar__spinner');
  const modal = root.querySelector('[data-staff-mode-modal]');
  const modalOk = root.querySelector('[data-staff-mode-modal-ok]');
  const modalCancelButtons = root.querySelectorAll('[data-staff-mode-modal-cancel]');

  // Set once per init() so the redirect-loop guard can tell "this load is the post-login landing"
  // apart from a later normal page load, even after removeAuthCodeFromUrl() strips auth_code
  // from the visible URL.
  let isPostLoginLanding = false;

  init();

  async function init() {
    bindEvents();

    const authCode = new URLSearchParams(window.location.search).get('auth_code');
    isPostLoginLanding = Boolean(authCode);

    if (authCode) {
      try {
        const data = await exchangeAuthCode(authCode);
        saveStoreContext(data);
        removeAuthCodeFromUrl();
      } catch (error) {
        console.warn('[c_staff-mode-header-bar] auth_code exchange failed', error);
        // Strip the failed/consumed auth_code first: if the login URL setting is blank,
        // redirectToLogin reloads the current page — leaving auth_code in the URL would
        // re-enter this catch on every load (infinite reload loop). With it removed, the
        // reload lands on a clean EC page; with a configured URL we navigate away anyway.
        stripAuthCodeFromUrl();
        redirectToLogin('exchange_failed');
        return;
      }
    }

    if (authCode || accessToken) {
      const storeContext = getStoredStoreContext();
      renderStoreContext(storeContext);
      showBar();
    }

    // Per-page session validation (once per load — never on AJAX/facet nav, see bindEvents/other
    // consumers which don't re-run init()). Fixes the "zombie bar": a token that got invalid
    // server-side (expired exp, disabled account) used to keep showing the bar until logout.
    // Skip on the post-login landing: exchangeAuthCode() above already minted a fresh token and
    // rendered the bar, so re-validating it immediately is a redundant round-trip.
    if (accessToken && !isPostLoginLanding) {
      await validateStaffSession();
    }
  }

  function bindEvents() {
    if (menuButton && menuPanel) {
      menuButton.addEventListener('click', () => toggleMenu());
      document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) closeMenu();
      });
    }

    clearButton?.addEventListener('click', () => {
      if (clearButton.disabled) return;
      openModal();
    });

    modalOk?.addEventListener('click', () => clearCustomerSession());
    modalCancelButtons.forEach((button) => button.addEventListener('click', closeModal));

    logoutButton?.addEventListener('click', () => logoutStaffMode());

    // Cart state → header bar color (spec §2.1.7). cart:refresh is the universal cart-change
    // signal on this theme: it is the ONLY event the custom PDP add-to-cart (.js-add-to-cart in
    // c_sku-cart-add.liquid) emits, and Focal dispatches it on every mutation too — so it is what
    // actually reaches the bar on add. variant:added is Focal's explicit add signal (kept for
    // templates using <product-form>). Latched: any cart event with items arms the dirty flag
    // (red); emptying does NOT clear it — only the clear button / logout / 401 do.
    document.documentElement.addEventListener('variant:added', onCartVariantAdded);
    document.documentElement.addEventListener('cart:updated', onCartChange);
    document.documentElement.addEventListener('cart:refresh', onCartChange);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        closeModal();
      }
    });

    window.addEventListener('resize', updateBarHeightVar);
  }

  async function exchangeAuthCode(authCode) {
    const response = await fetch(`${apiBase}/staff-mode/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ authCode }),
    });

    if (!response.ok) throw new Error(`exchange failed: ${response.status}`);

    const data = await response.json().catch(() => ({}));
    const exchangeData = normalizeExchangeData(data);

    if (exchangeData.accessToken) {
      accessToken = exchangeData.accessToken;
      if (window.CStaffSession) CStaffSession.setToken(accessToken);
    }
    return exchangeData;
  }

  // Validates the cached token against the backend on this page load. 200 = still a valid
  // session: refresh only the displayed store name (see updateStoreName — the cached
  // sq_location_id/store_code from exchange must NOT be overwritten, cart pickup preselect
  // depends on them and /staff-mode/store deliberately doesn't return sq_location_id).
  // 401 = session invalid (expired exp or disabled account) → full expiry cleanup.
  // Anything else (network failure, 5xx) is treated as a transient blip: keep the cached
  // context/bar as-is and don't redirect, so a flaky connection never kicks a working staff mid-shift.
  async function validateStaffSession() {
    let response;
    try {
      response = await staffModeFetch(`${apiBase}/staff-mode/store`, { method: 'GET' });
    } catch (error) {
      // fetch() only rejects on a network-level failure (offline, DNS, CORS, aborted) — never for
      // a completed HTTP exchange, even 4xx/5xx. A 401 always resolves and is handled below.
      console.warn('[c_staff-mode-header-bar] store validate network error, keeping cached session', error);
      return;
    }

    if (response.status === 401) {
      // Session invalid (expired exp or disabled account) → full expiry cleanup.
      handleSessionExpired();
      return;
    }

    if (!response.ok) {
      console.warn(`[c_staff-mode-header-bar] store validate failed: ${response.status}`);
      return;
    }

    // Session is valid (any 2xx) → clear the expiry redirect-loop guard so a later genuine
    // expiry can redirect again. Tie this to response.ok, NOT to store_name presence: a 200
    // with a missing name is still a valid session.
    try { window.sessionStorage.removeItem(SESSION_EXPIRED_GUARD_KEY); } catch (error) { /* best-effort */ }

    const resData = await response.json().catch(() => ({}));
    const name = resData?.data?.storeName;
    if (name) updateStoreName(name);
  }

  function updateStoreName(name) {
    if (storeName) storeName.textContent = name;
  }

  // 401 cleanup on token expiry. Per Sota's decision (tabio-document#299, 2026-07-10):
  // clear the EC customer session too — same as the explicit logout button (§2.1.4) — so the
  // next order can't be mis-attributed to the previous customer. Safe here because the staff
  // token is still in the cookie (browser not closed), so we can reliably detect "this browser
  // was in staff mode". (Browser-close stays "keep customer" — no trace survives to detect it.)
  // The login URL now comes solely from the theme setting (data-login-url); a blank/invalid
  // setting makes buildLoginUrl return null, and goToLoginOrReload/logoutCustomerViaIframe
  // reload the current page instead — the EC-customer clear below still runs either way.
  async function handleSessionExpired() {
    // await so the cart cleanup settles before the EC-customer logout (iframe) below — same race
    // prevention as finishStaffLogout / clearCustomerSession.
    await clearStaffModeSessionState();
    hideBar();
    document.documentElement.classList.remove('staff-mode');

    let alreadyRedirectedForExpiry = false;
    try {
      alreadyRedirectedForExpiry = window.sessionStorage.getItem(SESSION_EXPIRED_GUARD_KEY) === '1';
    } catch (error) {
      // sessionStorage unavailable (privacy mode, etc.): fail open — allow the single redirect below.
    }

    // F3 redirect-loop guard: don't redirect (nor clear the EC customer) if this load is already
    // the post-login landing (fresh token via auth_code exchange but /staff-mode/store STILL 401 —
    // not a real expiry) or we already redirected once for session_expired since the last success.
    // Gating the customer-clear on the same guard avoids repeatedly logging the customer out in a loop.
    if (isPostLoginLanding || alreadyRedirectedForExpiry) return;

    try { window.sessionStorage.setItem(SESSION_EXPIRED_GUARD_KEY, '1'); } catch (error) { /* best-effort */ }

    const target = buildLoginUrl('session_expired');
    // If an EC customer is logged in, sign them out (§2.1.4 parity) before landing on the login
    // screen; logoutCustomerViaIframe navigates to `target` (or reloads if null) once
    // /account/logout completes. The clear runs unconditionally — never gate it on `target`.
    if (root.dataset.customerLoggedIn === 'true') {
      logoutCustomerViaIframe(target);
    } else {
      goToLoginOrReload(target);
    }
  }

  function renderStoreContext(data) {
    const name = data.storeName || data.store_name || data.store?.storeName || data.store?.store_name || '—';
    if (storeName) storeName.textContent = name;
    if (name === '—') console.warn('[c_staff-mode-header-bar] store_name is missing');
  }

  function saveStoreContext(data) {
    const context = {
      storeName: data.storeName || data.store_name || data.store?.storeName || data.store?.store_name || '',
      storeCode: data.storeCode || data.store_code || data.store?.storeCode || data.store?.store_code || '',
      // SQ location id used by the pickup selection screen to pre-select the staff's store.
      sqLocationId: data.sqLocationId || data.sq_location_id || data.store?.sqLocationId || data.store?.sq_location_id || '',
    };
    if (window.CStaffSession) CStaffSession.setStoreContext(context);
  }

  function normalizeExchangeData(responseData) {
    const data = responseData.data || responseData;
    const store = data.store || {};

    return {
      accessToken: data.accessToken || data.access_token || responseData.accessToken || responseData.access_token || '',
      storeName: store.storeName || store.store_name || data.storeName || data.store_name || '',
      storeCode: store.storeCode || store.store_code || data.storeCode || data.store_code || '',
      sqLocationId: store.sqLocationId || store.sq_location_id || data.sqLocationId || data.sq_location_id || '',
    };
  }

  function getStoredStoreContext() {
    return window.CStaffSession ? CStaffSession.getStoreContext() : {};
  }

  function showBar() {
    root.hidden = false;
    // A member present at show time means customer info exists → mark dirty (spec §2.1.7).
    if (root.dataset.customerLoggedIn === 'true' && window.CStaffSession) CStaffSession.setNeedsClear();
    recomputeBarColor();
    updateBarHeightVar();
  }

  function hideBar() {
    root.hidden = true;
    document.documentElement.style.setProperty('--staff-mode-bar-height', '0px');
  }

  // Publishes this bar's rendered height as a CSS custom property so Focal's sticky header
  // (position:sticky; top:0, same as this bar) can offset below it instead of both fighting
  // for the same spot on scroll — same pattern/limitation as Focal's own
  // --announcement-bar-height (sections/announcement-bar.liquid): measured on
  // show/resize, not via ResizeObserver, so a mid-session breakpoint change (e.g. rotating
  // a tablet without reloading) can leave it briefly stale until the next resize event.
  function updateBarHeightVar() {
    if (root.hidden) return;
    document.documentElement.style.setProperty('--staff-mode-bar-height', root.clientHeight + 'px');
  }

  function isStaffModeActive() {
    return Boolean(accessToken);
  }

  // Apply the latched color (red = dirty ∨ member ∨ cart>0; spec §2.1.7) and the clear
  // button's enabled state via the tested pure unit (c_staff-mode-color.js). The dirty flag
  // is read from the session cookie — that's what keeps the bar red after a cart is emptied.
  function recomputeBarColor() {
    if (!window.CStaffModeColor) return null;
    return CStaffModeColor.recomputeBarColor(root, {
      getDirty: function () { return !!(window.CStaffSession && CStaffSession.getNeedsClear()); },
      clearButton: clearButton,
    });
  }

  // variant:added fires on a real add-to-cart (product form / quick view). Set the dirty
  // flag so the bar goes red. Guarded: never dirty the bar for normal (non-staff) EC users.
  function onCartVariantAdded() {
    if (!isStaffModeActive()) return;
    if (window.CStaffSession) CStaffSession.setNeedsClear();
    recomputeBarColor();
  }

  // cart:updated / cart:refresh fire on any cart change (add/remove/qty) and carry detail.cart.
  // reduceCartEvent decides, from the payload, whether to ARM the dirty latch (any items present
  // → arm; spec §2.1.7 guest-latch extension) and what count to persist to the dataset. The flag
  // is NEVER cleared here (latch: emptying the cart must not return the bar to blue) — only the
  // clear button / logout / 401 clear it. A payload-less event (the clear button's own
  // cart:refresh, dispatched without detail.cart) neither arms nor updates the count, so the
  // clear flow cannot re-arm the latch it just cleared.
  function onCartChange(event) {
    if (!isStaffModeActive()) return;
    var count = event && event.detail && event.detail.cart && event.detail.cart.item_count;
    var decision = window.CStaffModeColor ? CStaffModeColor.reduceCartEvent({ itemCount: count }) : null;
    if (decision) {
      if (decision.armLatch && window.CStaffSession) CStaffSession.setNeedsClear();
      if (decision.itemCount !== null) root.dataset.cartItemCount = String(decision.itemCount);
    }
    recomputeBarColor();
  }

  async function logoutStaffMode() {
    setLogoutLoading(true);
    try {
      const response = await Promise.race([
        staffModeFetch(`${apiBase}/staff-mode/logout`, { method: 'POST' }),
        timeout(5000),
      ]);

      if (!response.ok) throw new Error(`logout failed: ${response.status}`);

      finishStaffLogout(buildLoginUrl());
    } catch (error) {
      // §2.1.6: even on a logout API 401 / network failure, still tear down the client
      // session and redirect to login so the staff UI never gets stuck "logged in".
      console.warn('[c_staff-mode-header-bar] logout failed; clearing client session anyway', error);
      finishStaffLogout(buildLoginUrl('logout_failed'));
    }
  }

  // Client-side logout teardown — shared by the success path and the §2.1.6 failure path.
  // Clears the staff session (token + 客注 cart attrs) and, if an EC customer is logged in,
  // logs them out too (§3.3) before landing on `target` (or reloading if it's null).
  async function finishStaffLogout(target) {
    // await so the cart cleanup completes before we log the EC customer out (iframe) or redirect —
    // prevents the in-flight cart POST from resurrecting the customer session after logout.
    await clearStaffModeSessionState();
    if (root.dataset.customerLoggedIn === 'true') {
      logoutCustomerViaIframe(target);
    } else {
      goToLoginOrReload(target);
    }
  }

  function logoutCustomerViaIframe(redirectUrl) {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';

    let timer;
    const handleRedirect = () => {
      clearTimeout(timer);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      goToLoginOrReload(redirectUrl);
    };

    iframe.onload = handleRedirect;
    iframe.onerror = handleRedirect;

    timer = setTimeout(handleRedirect, 10000);

    iframe.src = '/account/logout';
    document.body.appendChild(iframe);
  }

  // "Clear customer info" OK handler (spec §2.1.4.1). One button branches on member state at
  // press time, then RELOADS the page so the next customer starts from a clean, Liquid-rendered
  // state (staff mode itself is unaffected — the staff token is separate and survives):
  //  - member: log the EC customer out via a hidden iframe (/account/logout) — destroys the
  //    server session — AWAITED so the reload below can't cancel it mid-flight (the §#299 race).
  //  - guest: no customer to log out.
  // Both reset the cart token (new empty cart; also drops the abandoned-checkout input bound to
  // the old token) and clear the dirty flag, then reload. The in-place dataset/cart:refresh/
  // recompute are a transient pre-reload fallback so the bar flashes blue even if the reload is
  // slow; the reload is what actually re-renders the clean state.
  // Serialised: await the cart-attr fallback BEFORE resetCartToken so its in-flight response
  // (which may carry Set-Cookie: cart=...) settles — the reset then deletes that freshly-set
  // cookie instead of racing it (same race fixed for logout/clear in §#299).
  async function clearCustomerSession() {
    closeModal();
    const wasMember = root.dataset.customerLoggedIn === 'true';
    await clearStaffModeCartAttributes();   // race-safety fallback (§#299)
    resetCartToken();                        // primary: discard cart cookie → fresh empty cart
    if (window.CStaffSession) CStaffSession.clearNeedsClear();
    if (wasMember) await logoutCustomerNoNav(); // member-only: server-side EC customer logout (awaited → reload can't cut it off)
    root.dataset.customerLoggedIn = 'false';
    root.dataset.cartItemCount = '0';
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    recomputeBarColor();                     // transient pre-reload fallback → blue
    window.location.reload();                // clean Liquid-rendered state for the next customer
  }

  // Log the EC customer out WITHOUT navigating the top page. A hidden same-origin iframe loads
  // /account/logout; the server clears the customer session cookie during that request, so later
  // top-level requests are anonymous and the next customer isn't mis-attributed (spec §2.1.4).
  // Returns a Promise that resolves once the iframe load/error/timeout fires, so clearCustomerSession
  // can AWAIT it before reloading — otherwise the reload (a top-level navigation) would cancel the
  // in-flight /account/logout request and leave the customer session alive (the §#299 race).
  function logoutCustomerNoNav() {
    return new Promise(function (resolve) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      let settled = false;
      let timer;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        resolve();
      };
      iframe.onload = done;
      iframe.onerror = done;
      timer = setTimeout(done, 10000);
      iframe.src = '/account/logout';
      document.body.appendChild(iframe);
    });
  }

  function staffModeFetch(url, options) {
    const headers = new Headers(options.headers || {});
    if (accessToken) headers.set('X-Staff-Mode-Token', accessToken);

    return fetch(url, {
      ...options,
      headers,
      credentials: 'same-origin',
    });
  }

  function toggleMenu() {
    const shouldOpen = menuPanel.hidden;
    menuPanel.hidden = !shouldOpen;
    menuButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  }

  function closeMenu() {
    if (!menuPanel || menuPanel.hidden) return;
    menuPanel.hidden = true;
    menuButton?.setAttribute('aria-expanded', 'false');
  }

  function openModal() {
    if (modal) modal.hidden = false;
  }

  function closeModal() {
    if (modal) modal.hidden = true;
  }

  function setLogoutLoading(isLoading) {
    if (logoutButton) logoutButton.disabled = isLoading;
  }

  // Removes auth_code from the visible URL without stamping mode=tabiosearch — used on the
  // exchange-failure path, where the staff never entered staff mode (so the analytics-exclusion
  // param doesn't apply) and a lingering auth_code would loop a blank-setting reload.
  function stripAuthCodeFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_code');
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }

  function removeAuthCodeFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_code');
    // Staff mode is now active (token saved during exchange). Stamp the carry-over param on this first
    // post-login page so GA/KARTE can exclude it as early as possible; subsequent pages are handled by
    // the synchronous head script in theme.liquid. (That patched replaceState would also add it, but we
    // set it explicitly so this works even if the head script is absent.)
    url.searchParams.set('mode', 'tabiosearch');
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  }

  function clearStaffModeToken() {
    accessToken = '';
    if (window.CStaffSession) CStaffSession.clear();
  }

  // Shared teardown (DRY) used by both the explicit logout button and the 401 expiry path in
  // validateStaffSession/handleSessionExpired: drop the staff token + hand the next customer a clean
  // cart. resetCartToken() is primary (new empty cart, also clears the resumed checkout input);
  // clearStaffModeCartAttributes() is the server-side fallback for the 客注 attrs (§#299).
  // async: the /cart/update.js response must settle BEFORE resetCartToken() and BEFORE the customer
  // logout that follows in the callers (finishStaffLogout / handleSessionExpired). Otherwise the
  // in-flight keepalive cart POST can land a Set-Cookie AFTER the customer logout cleared the EC
  // session and resurrect it (same race fixed in clearCustomerSession). Serialising keeps the
  // §2.1.4/§3.3 EC-customer logout intact — the previous customer must NOT survive into the next session.
  async function clearStaffModeSessionState() {
    clearStaffModeToken();
    await clearStaffModeCartAttributes();
    resetCartToken();
  }

  // Reset the Shopify cart token so logout / clear-member / 401-expiry hand the next customer a
  // brand-new empty cart. Clearing the `cart` cookie (host-only, Path=/, NOT HttpOnly — set by
  // Shopify) makes the next request mint a fresh cart. Crucially this also discards the abandoned
  // checkout tied to the old token, so the previous customer's typed contact/address ("input value"
  // resume) no longer reappears — that data is bound to the cart token, NOT the line items, so
  // deleting items or clearing cart attributes alone does not remove it (verified on staging).
  // This is the PRIMARY cart cleanup; clearStaffModeCartAttributes() below is a server-side fallback.
  // Synchronous cookie write (no fetch/keepalive) → always completes before the caller navigates.
  function resetCartToken() {
    ['cart', 'cart_sig', 'cart_ver'].forEach((name) => {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
    });
  }

  // FALLBACK (defense-in-depth) to resetCartToken() — per Sota's decision (tabio-document#299,
  // 2026-07-10). Best-effort server-side clear of the 客注 cart attribute keys owned by
  // assets/c_main-cart.js (OWNED_ATTR_KEYS) — duplicated here rather than imported since that file
  // is out of scope; keep both lists in sync if the key set ever changes. Only actually matters if
  // the cart-cookie reset above failed to take effect (cookie still present → this fetch clears the
  // stale attrs on the still-active old cart). keepalive so it survives the immediate navigation.
  // Absolute-URL construction: a third-party app (Pulse embroidery app) monkey-patches
  // window.fetch and hijacks requests matching the relative prefix "/cart/update". This call
  // site's body is attributes-only so there's no live data-loss risk here; this is preventive
  // standardization to match the pattern used theme-wide. An absolute URL doesn't match that
  // prefix and reaches Shopify directly. Hoisted `function` (not const/arrow) because
  // window.CStaffModeHeaderBar above references it before this declaration runs.
  function cartUpdateUrl() {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const base = window.themeVariables?.routes?.cartUpdateUrl || `${root}/cart/update`;
    const pathname = `${base}.js`;
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    return `${origin}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
  }

  function clearStaffModeCartAttributes() {
    const cleared = {};
    STAFF_MODE_ATTR_KEYS.forEach((k) => { cleared[k] = ''; });

    // Returns the fetch promise so awaiting callers (clearCustomerSession) can serialise before
    // navigating; the .catch keeps it non-rejecting so `await` never throws. The fire-and-forget
    // callers (clearStaffModeSessionState) simply ignore the returned promise — behaviour unchanged.
    // keepalive kept so those fire-and-forget call sites still survive their immediate navigation.
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ attributes: cleared }),
    }).catch((error) => console.warn('[c_staff-mode-header-bar] cart attribute cleanup failed', error));
  }

  // Builds the login URL string from the theme setting (`loginUrl`, rendered from
  // settings.staff_mode_login_url into data-login-url). Returns null when the setting is blank,
  // malformed, or uses a non-http(s) protocol — callers then reload the page instead. Never
  // throws: a bad setting must not abort the caller (the §2.1.4/#299 EC-customer clear on expiry
  // runs after this and must not be skipped). Absolute (external) and relative URLs both resolve.
  function buildLoginUrl(reason) {
    if (!loginUrl) {
      console.error('[c_staff-mode-header-bar] staff_mode_login_url not configured');
      return null;
    }
    let url;
    try {
      url = new URL(loginUrl, window.location.origin);
    } catch (error) {
      console.error('[c_staff-mode-header-bar] staff_mode_login_url is not a valid URL', error);
      return null;
    }
    // Protocol allowlist: new URL() accepts javascript:/data:/vbscript: and Shopify's `url`
    // setting doesn't sanitize the protocol, so block anything but http(s) before navigating.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      console.error(`[c_staff-mode-header-bar] staff_mode_login_url has a disallowed protocol: ${url.protocol}`);
      return null;
    }
    if (reason) url.searchParams.set('reason', reason);
    return url.toString();
  }

  // Navigate to the login URL, or reload the current page when it's null (blank/invalid setting).
  // Reload is the graceful fallback: OPS fixes the theme setting and the staff re-enters staff mode.
  function goToLoginOrReload(target) {
    if (target) {
      window.location.href = target;
    } else {
      window.location.reload();
    }
  }

  function redirectToLogin(reason) {
    goToLoginOrReload(buildLoginUrl(reason));
  }

  function timeout(ms) {
    return new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('timeout')), ms);
    });
  }
})();
